import { useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useBenchmarks, useCreateBenchmark, useUpdateBenchmark, useDeleteBenchmark,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { BenchmarkFormDialog } from './BenchmarkFormDialog'
import type { Benchmark } from '@/modules/multichannel/types/configuracion'

export function BenchmarksTab() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Benchmark | null>(null)

  const { data: benchmarks, isLoading } = useBenchmarks()
  const createMutation = useCreateBenchmark()
  const updateMutation = useUpdateBenchmark()
  const deleteMutation = useDeleteBenchmark()

  return (
    <div className="space-y-4">
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Benchmarks de Metricas
              {benchmarks && <Badge className="badge-gradient text-xs">{benchmarks.length}</Badge>}
            </CardTitle>
            <Button onClick={() => { setEditing(null); setDialogOpen(true) }} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" /> Nuevo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : benchmarks && benchmarks.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Tasa Entrega</TableHead>
                    <TableHead>Tasa Aperturas</TableHead>
                    <TableHead>Tasa Clics</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benchmarks.map(bm => (
                    <TableRow key={bm.id}>
                      <TableCell className="font-medium">{bm.categoria}</TableCell>
                      <TableCell><Badge variant="outline">{bm.tasa_entrega}%</Badge></TableCell>
                      <TableCell><Badge variant="outline">{bm.tasa_aperturas}%</Badge></TableCell>
                      <TableCell><Badge variant="outline">{bm.tasa_clics}%</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{bm.descripcion || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={bm.activo}
                            onCheckedChange={() => updateMutation.mutate({ id: bm.id, data: { activo: !bm.activo } })}
                            className={bm.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                          <Badge className={bm.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {bm.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(bm); setDialogOpen(true) }}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm(`Eliminar benchmark "${bm.categoria}"?`)) await deleteMutation.mutateAsync(bm.id)
                          }}><Trash2 className="h-4 w-4 text-gray-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">No hay benchmarks creados.</div>
          )}
        </CardContent>
      </Card>

      <BenchmarkFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null) }}
        benchmark={editing}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
